import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { withKey, API_BASE } from '../services/utils';

const AUDIENCE_STORAGE_KEY = 'documentation_audience';

const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <h1 className="mb-4 text-2xl font-bold text-black/90">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-3 mt-8 text-xl font-semibold text-black/90">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-2 mt-6 text-base font-semibold text-black/80">{children}</h3>,
  p: ({ children }) => <p className="mb-4 leading-relaxed text-black/80">{children}</p>,
  ul: ({ children }) => <ul className="mb-4 list-disc space-y-1 pl-6 text-black/80">{children}</ul>,
  ol: ({ children }) => <ol className="mb-4 list-decimal space-y-1 pl-6 text-black/80">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-black/90">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="mb-4 border-l-4 border-company-main/40 pl-4 text-black/60">{children}</blockquote>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="rounded bg-black/5 px-1.5 py-0.5 font-mono text-sm text-black/80">{children}</code>
    ) : (
      <code className="font-mono text-sm">{children}</code>
    ),
  pre: ({ children }) => (
    <pre className="mb-4 overflow-x-auto rounded-md bg-black/5 p-4 text-sm">{children}</pre>
  ),
  table: ({ children }) => (
    <div className="mb-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b-2 border-black/10 text-left">{children}</thead>,
  th: ({ children }) => <th className="py-2 pr-4 font-semibold text-black/80">{children}</th>,
  td: ({ children }) => <td className="border-b border-black/5 py-2 pr-4 text-black/70">{children}</td>,
  hr: () => <hr className="my-8 border-black/10" />,
};

function Documentation() {
  const [audience, setAudience] = useState(
    () => localStorage.getItem(AUDIENCE_STORAGE_KEY) || 'system'
  );
  const [topics, setTopics] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'forbidden' | 'error'

  useEffect(() => {
    localStorage.setItem(AUDIENCE_STORAGE_KEY, audience);
  }, [audience]);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    (async () => {
      try {
        const url = await withKey(`${API_BASE}/api/docs?audience=${audience}`);
        const response = await fetch(url);
        if (cancelled) return;
        if (response.status === 403) {
          setStatus('forbidden');
          return;
        }
        if (!response.ok) {
          setStatus('error');
          return;
        }
        const data = await response.json();
        setTopics(data.topics || []);
        setSelectedSlug((data.topics || [])[0]?.slug || null);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [audience]);

  const selectedTopic = useMemo(
    () => topics.find(t => t.slug === selectedSlug) || null,
    [topics, selectedSlug]
  );

  const linkRenderer = ({ href, children }) => {
    const match = href && href.endsWith('.md')
      ? topics.find(t => href === `${t.slug}.md` || href.endsWith(`/${t.slug}.md`))
      : null;
    if (match) {
      return (
        <a
          href={`#${match.slug}`}
          onClick={(e) => { e.preventDefault(); setSelectedSlug(match.slug); }}
          className="text-company-main underline hover:text-company-main/80"
        >
          {children}
        </a>
      );
    }
    if (href && href.endsWith('.md')) {
      // Cross-reference to a doc not included in this audience's topic list.
      return <span className="text-black/50" title="Ikke tilgængelig her">{children}</span>;
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-company-main underline hover:text-company-main/80"
      >
        {children}
      </a>
    );
  };

  return (
    <div className="flex h-screen bg-white font-sans">
      <aside className="flex w-72 shrink-0 flex-col border-r border-black/10 bg-company-toned">
        <div className="border-b border-black/10 p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-company-main text-sm font-bold text-white">
              80
            </div>
            <div className="text-sm font-semibold text-black/80">Dokumentation</div>
          </div>
          <div className="flex rounded-md bg-black/5 p-1 text-sm">
            <button
              type="button"
              onClick={() => setAudience('system')}
              className={`flex-1 rounded px-2 py-1.5 font-medium transition ${
                audience === 'system' ? 'bg-white text-company-main shadow-sm' : 'text-black/50 hover:text-black/70'
              }`}
            >
              Til brugere
            </button>
            <button
              type="button"
              onClick={() => setAudience('dev')}
              className={`flex-1 rounded px-2 py-1.5 font-medium transition ${
                audience === 'dev' ? 'bg-white text-company-main shadow-sm' : 'text-black/50 hover:text-black/70'
              }`}
            >
              Til udviklere
            </button>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {topics.map(topic => (
            <button
              key={topic.slug}
              type="button"
              onClick={() => setSelectedSlug(topic.slug)}
              className={`mb-1 block w-full rounded px-3 py-2 text-left text-sm transition ${
                selectedSlug === topic.slug
                  ? 'bg-company-main font-medium text-white'
                  : 'text-black/70 hover:bg-black/5'
              }`}
            >
              {topic.title}
            </button>
          ))}
        </nav>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-8 py-12">
          {status === 'loading' && <p className="text-sm text-black/50">Indlæser…</p>}
          {status === 'forbidden' && (
            <div className="rounded-md border border-black/10 bg-black/5 p-6 text-sm text-black/70">
              Du har ikke adgang til at se dokumentationen. Åbn siden igen via linket i Zoho.
            </div>
          )}
          {status === 'error' && (
            <div className="rounded-md border border-black/10 bg-black/5 p-6 text-sm text-black/70">
              Dokumentationen kunne ikke indlæses. Prøv at genindlæse siden.
            </div>
          )}
          {status === 'ready' && selectedTopic && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ ...MARKDOWN_COMPONENTS, a: linkRenderer }}>
              {selectedTopic.content}
            </ReactMarkdown>
          )}
        </div>
      </main>
    </div>
  );
}

export default Documentation;
